import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useDebounce } from '@/hooks/use-debounce';
import { Check, ChevronsUpDown, Loader2, Search } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

export interface Customer {
  id: number;
  name: string;
  email: string | null;
  phone: string | null;
  address: string | null;
}

interface SearchSelectProps {
  onSelect: (customer: Customer) => void;
}

export function SearchSelect({ onSelect }: SearchSelectProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  const debouncedSearch = useDebounce(searchValue, 300);

  const { data: customers, isLoading } = useQuery({
    queryKey: ['/api/customers', debouncedSearch],
    staleTime: 1000 * 60 * 5,
    enabled: debouncedSearch.length >= 2,
    queryFn: async () => {
      const searchParam = `?search=${encodeURIComponent(debouncedSearch)}&limit=20`;
      const result = await apiRequest('GET', `/api/customers${searchParam}`);
      return result;
    },
  });

  const filteredCustomers = customers;
  const showPrompt = debouncedSearch.length < 2;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between"
        >
          {searchValue || 'Search existing contacts...'}
          <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search by name, email or phone..."
            value={searchValue}
            onValueChange={setSearchValue}
          />
          {isLoading ? (
            <div className="flex items-center justify-center p-4 text-sm">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Loading contacts...
            </div>
          ) : showPrompt ? (
            <div className="flex flex-col items-center justify-center p-6 text-sm text-muted-foreground">
              <Search className="mb-2 h-5 w-5 opacity-40" />
              Type at least 2 characters to search...
            </div>
          ) : (
            <>
              <CommandEmpty>No contacts found.</CommandEmpty>
              <CommandGroup className="max-h-[300px] overflow-auto">
                {filteredCustomers?.map((customer: Customer) => (
                  <CommandItem
                    key={customer.id}
                    value={customer.name}
                    onSelect={() => {
                      onSelect(customer);
                      setSearchValue(customer.name);
                      setOpen(false);
                    }}
                    className="flex items-center gap-3 p-3"
                  >
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30 text-sm font-semibold text-blue-700 dark:text-blue-300">
                      {customer.name
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                        .slice(0, 2)
                        .toUpperCase()}
                    </div>
                    <div className="flex flex-col min-w-0 flex-1">
                      <span className="font-medium truncate">{customer.name}</span>
                      <span className="text-xs text-muted-foreground truncate">
                        {[customer.phone, customer.email].filter(Boolean).join(' · ') ||
                          'No contact details'}
                      </span>
                    </div>
                    <Check
                      className={cn(
                        'ml-auto h-4 w-4 shrink-0',
                        searchValue === customer.name ? 'opacity-100' : 'opacity-0'
                      )}
                    />
                  </CommandItem>
                ))}
              </CommandGroup>
            </>
          )}
        </Command>
      </PopoverContent>
    </Popover>
  );
}
